<template>
  <component :is="embedded ? 'div' : 'v-container'" :fluid="!embedded" :class="embedded ? 'intelligence-jd-match' : 'intelligence-jd-match pa-4'">
    <v-row>
      <v-col cols="12">
        <h1 class="text-h5 font-weight-bold mb-1 theme-heading">
          JD Match
        </h1>
        <p class="text-subtitle-2 grey--text text--darken-1 mb-4">
          Paste a job description and find the best internal candidates
        </p>
      </v-col>
    </v-row>

    <v-alert v-if="hasError" type="error" class="mb-4">
      <div class="d-flex align-center justify-space-between">
        <span>{{ errorMessage }}</span>
        <v-btn small outlined color="error" class="ml-3" @click="matchCandidates">
          <v-icon small left>mdi-refresh</v-icon>
          Retry
        </v-btn>
      </div>
    </v-alert>

    <v-row>
      <v-col cols="12" md="5">
        <v-card outlined class="pa-4">
          <v-textarea
            v-model="jobDescription"
            label="Paste job description here..."
            outlined
            rows="12"
            color="#007AFF"
            hide-details
            class="mb-4"
          />

          <v-textarea
            v-model="recruiterNotes"
            label="Recruiter Notes (optional)"
            placeholder="Add context, must-have skills, or preferences not in the JD... e.g., 'Must have Python and AWS. Team is fully remote. Hiring manager prefers startup experience.'"
            outlined
            dense
            rows="3"
            color="#007AFF"
            hide-details
            class="mb-4"
          />
          <div v-if="recruiterNotes" class="d-flex align-center justify-space-between mb-3">
            <span class="text-caption grey--text font-italic">
              Skills and preferences from your notes will be extracted and weighted in matching
            </span>
            <v-btn x-small text color="grey" @click="recruiterNotes = ''">
              Clear
            </v-btn>
          </div>

          <v-btn
            block
            color="#007AFF"
            dark
            :loading="isLoading"
            :disabled="!jobDescription.trim()"
            @click="matchCandidates"
          >
            <v-icon left>mdi-account-search</v-icon>
            Match Candidates
          </v-btn>
        </v-card>
      </v-col>

      <v-col cols="12" md="7">
        <div v-if="!hasResults && !isLoading">
          <v-card outlined class="pa-6 text-center mb-4">
            <v-icon size="64" color="#E0E0E0" class="mb-3">mdi-file-document-outline</v-icon>
            <h3 class="text-subtitle-1 font-weight-bold mb-2 theme-heading">
              How JD Match Works
            </h3>
            <div class="text-body-2 grey--text text--darken-1 mb-4" style="max-width: 400px; margin: 0 auto">
              Paste any job description and our engine will systematically match
              internal talent based on skills, experience depth, seniority, and
              functional area alignment.
            </div>
            <v-row dense class="justify-center">
              <v-col cols="auto" v-for="feature in features" :key="feature.label">
                <v-chip small outlined color="#007AFF" class="ma-1">
                  <v-icon x-small left>{{ feature.icon }}</v-icon>
                  {{ feature.label }}
                </v-chip>
              </v-col>
            </v-row>
          </v-card>
        </div>

        <div v-if="hasResults">
          <div class="d-flex align-center justify-space-between mb-3">
            <div class="text-caption grey--text text--darken-1">
              {{ results.length }} candidates matched
              <span v-if="executionTimeMs"> in {{ executionTimeMs }}ms</span>
            </div>
            <v-chip small color="#10B981" text-color="white" v-if="topMatches.length > 0">
              {{ topMatches.length }} strong/good matches
            </v-chip>
          </div>

          <v-card
            v-if="extractedRequirements.length > 0"
            outlined
            class="mb-4 req-refinement-card"
          >
            <v-card-title
              class="py-3 px-4 req-header"
              style="cursor: pointer"
              @click="showReqEditor = !showReqEditor"
            >
              <div class="d-flex align-center flex-grow-1">
                <v-icon small color="#8b5cf6" class="mr-2">mdi-lightning-bolt</v-icon>
                <div>
                  <div class="text-subtitle-2 font-weight-bold">Refine Requirements</div>
                  <div class="text-caption grey--text">
                    Adjust importance and weight of each requirement, then re-evaluate
                  </div>
                </div>
              </div>
              <div class="d-flex align-center">
                <v-chip
                  v-for="req in extractedRequirements.slice(0, 4)"
                  :key="req.id"
                  x-small
                  class="mr-1"
                  :color="importanceColor(req.importance)"
                  :text-color="importanceTextColor(req.importance)"
                  :outlined="req.importance === 'nice-to-have'"
                >
                  {{ req.name && req.name.length > 20 ? req.name.substring(0, 20) + '...' : req.name }}
                </v-chip>
                <span v-if="extractedRequirements.length > 4" class="text-caption grey--text ml-1">
                  +{{ extractedRequirements.length - 4 }} more
                </span>
                <v-icon small class="ml-2">{{ showReqEditor ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </div>
            </v-card-title>

            <v-expand-transition>
              <div v-if="showReqEditor">
                <v-divider />
                <v-card-text class="pa-4 theme-surface">
                  <div
                    v-for="req in extractedRequirements"
                    :key="req.id"
                    class="req-row d-flex align-center pa-3 mb-2"
                  >
                    <div class="flex-grow-1 text-subtitle-2 font-weight-medium req-name theme-heading">
                      {{ req.name }}
                    </div>

                    <v-btn
                      x-small
                      depressed
                      class="mr-3 importance-btn"
                      :color="importanceBtnColor(req.importance)"
                      :dark="req.importance !== 'nice-to-have'"
                      :outlined="req.importance === 'nice-to-have'"
                      :disabled="isRescoring"
                      @click="cycleImportance(req.id)"
                    >
                      {{ importanceLabel(req.importance) }}
                    </v-btn>

                    <div class="d-flex align-center mr-3" style="min-width: 140px">
                      <v-slider
                        :value="req.weight"
                        @input="updateWeight(req.id, $event)"
                        min="0"
                        max="100"
                        hide-details
                        dense
                        color="#8b5cf6"
                        track-color="#e2e8f0"
                        :disabled="isRescoring"
                        style="max-width: 90px"
                      />
                      <span
                        class="text-caption font-weight-bold ml-2"
                        :style="{ color: req.weight >= 70 ? '#8b5cf6' : '#94a3b8', minWidth: '32px', textAlign: 'right' }"
                      >
                        {{ req.weight }}%
                      </span>
                    </div>

                    <v-btn
                      icon
                      x-small
                      color="red lighten-2"
                      :disabled="isRescoring"
                      @click="deleteRequirement(req.id)"
                    >
                      <v-icon x-small>mdi-close</v-icon>
                    </v-btn>
                  </div>

                  <v-divider class="my-3" />

                  <div class="d-flex justify-end">
                    <v-btn
                      small
                      text
                      color="grey"
                      class="mr-2"
                      @click="showReqEditor = false"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      small
                      color="#8b5cf6"
                      dark
                      :loading="isRescoring"
                      :disabled="extractedRequirements.length === 0"
                      @click="handleRescore"
                    >
                      <v-icon small left>mdi-refresh</v-icon>
                      Re-Evaluate with Changes
                    </v-btn>
                  </div>
                </v-card-text>
              </div>
            </v-expand-transition>
          </v-card>

          <JDMatchCard
            v-for="result in results"
            :key="result.profileId || result.name"
            :result="result"
            :job-title="extractedTitle"
            @view-profile="viewProfile"
          />
        </div>

        <v-card v-if="isLoading" outlined class="pa-6 text-center mb-4">
          <v-progress-circular indeterminate size="48" width="4" color="#007AFF" class="mb-4" />
          <h3 class="text-subtitle-1 font-weight-bold mb-2 theme-heading">
            Analyzing Job Description
          </h3>
          <p class="text-body-2 grey--text text--darken-1 mb-3">
            {{ jdLoadingStatusText }}
          </p>
          <v-progress-linear indeterminate color="#007AFF" rounded height="4" class="mb-2" style="max-width: 300px; margin: 0 auto" />
          <p class="text-caption grey--text" style="opacity: 0.6">This typically takes 5–15 seconds</p>
        </v-card>
      </v-col>
    </v-row>
  </component>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import JDMatchCard from "../../components/Intelligence/JDMatchCard.vue";

export default {
  name: "IntelligenceJDMatch",
  components: { JDMatchCard },
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showReqEditor: false,
      jdLoadingTextIndex: 0,
      jdLoadingInterval: null,
      features: [
        { label: "Skills Matching", icon: "mdi-code-tags" },
        { label: "Seniority Fit", icon: "mdi-account-arrow-up" },
        { label: "Experience Depth", icon: "mdi-chart-bar" },
        { label: "Functional Alignment", icon: "mdi-target" },
        { label: "Gap Analysis", icon: "mdi-puzzle" },
      ],
    };
  },
  computed: {
    ...mapState("intelligenceJDMatch", [
      "results",
      "totalCandidates",
      "executionTimeMs",
      "isLoading",
      "isRescoring",
      "hasError",
      "errorMessage",
      "extractedRequirements",
    ]),
    ...mapGetters("intelligenceJDMatch", ["hasResults", "topMatches"]),
    jobDescription: {
      get() {
        return this.$store.state.intelligenceJDMatch.jobDescription;
      },
      set(val) {
        this.$store.commit("intelligenceJDMatch/updateField", { path: "jobDescription", value: val });
      },
    },
    recruiterNotes: {
      get() {
        return this.$store.state.intelligenceJDMatch.recruiterNotes;
      },
      set(val) {
        this.$store.commit("intelligenceJDMatch/updateField", { path: "recruiterNotes", value: val });
      },
    },
    extractedTitle() {
      const firstLine = this.jobDescription.split("\n")[0] || "";
      return firstLine.substring(0, 60) || "Job Description";
    },
    jdLoadingStatusText() {
      var texts = [
        "Extracting requirements from job description...",
        "Analyzing profiles against extracted skills...",
        "Evaluating experience alignment...",
        "Finalizing candidate matches..."
      ];
      return texts[this.jdLoadingTextIndex] || texts[0];
    },
  },
  watch: {
    isLoading(val) {
      if (val) {
        this.jdLoadingTextIndex = 0;
        this.jdLoadingInterval = setInterval(() => {
          this.jdLoadingTextIndex = Math.min(this.jdLoadingTextIndex + 1, 3);
        }, 3000);
      } else {
        clearInterval(this.jdLoadingInterval);
        this.jdLoadingInterval = null;
      }
    },
  },
  beforeDestroy() {
    clearInterval(this.jdLoadingInterval);
  },
  methods: {
    ...mapActions("intelligenceJDMatch", ["matchJD", "rescoreJD"]),
    ...mapMutations("intelligenceJDMatch", [
      "cycleRequirementImportance",
      "updateRequirementWeight",
      "removeRequirement",
    ]),
    matchCandidates() {
      this.matchJD();
    },
    viewProfile(result) {
      this.$router.push({ name: "IntelligenceProfile", params: { profileId: result.profileId }, query: { from: 'jdmatch' } });
    },
    cycleImportance(reqId) {
      this.cycleRequirementImportance(reqId);
    },
    updateWeight(reqId, weight) {
      this.updateRequirementWeight({ reqId, weight });
    },
    deleteRequirement(reqId) {
      this.removeRequirement(reqId);
    },
    handleRescore() {
      this.rescoreJD();
    },
    importanceLabel(importance) {
      const labels = { "must-have": "Must-Have", "primary": "Primary", "nice-to-have": "Nice-to-Have" };
      return labels[importance] || "Primary";
    },
    importanceColor(importance) {
      const colors = { "must-have": "#ef4444", "primary": "#3b82f6", "nice-to-have": "#e2e8f0" };
      return colors[importance] || "#3b82f6";
    },
    importanceTextColor(importance) {
      return importance === "nice-to-have" ? "#64748b" : "white";
    },
    importanceBtnColor(importance) {
      const colors = { "must-have": "#ef4444", "primary": "#3b82f6", "nice-to-have": "#94a3b8" };
      return colors[importance] || "#3b82f6";
    },
  },
};
</script>

<style scoped>
.intelligence-jd-match {
  max-width: 1400px;
}
.req-refinement-card {
  border-color: rgba(139, 92, 246, 0.25);
}
.req-header:hover {
  background: rgba(139, 92, 246, 0.04);
}
.req-row {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  transition: box-shadow 0.2s ease;
}
.theme--dark .req-row {
  background: #1e1e2e;
  border-color: #444;
}
.theme--dark .req-name {
  color: #90CAF9 !important;
}
.req-row:hover {
  box-shadow: 0 2px 8px rgba(139, 92, 246, 0.1);
}
.req-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.importance-btn {
  text-transform: uppercase;
  font-size: 10px;
  letter-spacing: 0.05em;
  min-width: 80px;
}
</style>
